import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { observer } from "mobx-react-lite";
import CarbonActivityReportCard from "../components/submissions/CarbonActivityReportCard";
import { Textarea } from "@/components/ui/textarea";
import { PATHS } from "@/lib/router";
import { useCarbonBankSubmissionsStore } from "../hooks";
import { showToast } from "@/lib/utils/toast";
import { useTran, useFormDirty, useUnsavedChanges } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";

const StatusBadge = ({ status }) => {
  const { t } = useTran();
  if (status === "submitted")
    return (
      <Badge className="bg-emerald-600 text-white">{t("submitted")}</Badge>
    );
  if (status === "draft") return <Badge variant="outline">{t("draft")}</Badge>;
  return <Badge variant="outline">{t("none")}</Badge>;
};

const ReviewBadge = ({ status }) => {
  const { t } = useTran();
  if (status === "approved")
    return (
      <Badge className="bg-emerald-600 text-white">{t("bank approved")}</Badge>
    );
  if (status === "rejected")
    return (
      <Badge className="bg-rose-600 text-white">{t("bank rejected")}</Badge>
    );
  return <Badge variant="outline">{t("bank pending")}</Badge>;
};

const ConsentBadge = ({ consent }) => {
  const { t } = useTran();
  const status =
    consent?.lastRequest?.status || (consent?.canUse ? "approved" : "none");
  if (status === "approved")
    return <Badge className="bg-emerald-600 text-white">{t("approved")}</Badge>;
  if (status === "pending")
    return <Badge className="bg-amber-500 text-white">{t("pending")}</Badge>;
  return <Badge variant="outline">{t("none")}</Badge>;
};

const BankCarbonSubmissionsFormPage = observer(() => {
  const params = useParams();
  const navigate = useNavigate();
  const submissionsId = params.id;
  const { t } = useTran();

  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [year, setYear] = useState(null);
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState(() =>
    t(
      "Please acknowledge and approve consent for us to use your Carbon Inventory Survey data.",
    ),
  );
  const formValues = useMemo(
    () => ({ reviewNote, message }),
    [reviewNote, message],
  );
  const isDirty = useFormDirty(formValues, submissionsId);
  const { showDialog, confirmDiscard, cancelDiscard, bypass } =
    useUnsavedChanges(isDirty);

  const {
    carbonSubmissionsDetails,
    loadCarbonBankSubmissionsDetails,
    reviewCarbonSubmissionsReview,
  } = useCarbonBankSubmissionsStore();

  useEffect(() => {
    loadCarbonBankSubmissionsDetails(submissionsId);
  }, [submissionsId]);

  useEffect(() => {
    if (carbonSubmissionsDetails) {
      setDetail(carbonSubmissionsDetails);
    }
  }, [carbonSubmissionsDetails]);

  const normalizeData = (raw, baseName) => {
    const base = raw && typeof raw === "object" ? raw : {};
    const scope1 =
      base.scope1 && typeof base.scope1 === "object" ? base.scope1 : {};
    const scope2 =
      base.scope2 && typeof base.scope2 === "object" ? base.scope2 : {};
    const s1Activities =
      scope1.activities && typeof scope1.activities === "object"
        ? scope1.activities
        : {};
    const s2Activities =
      scope2.activities && typeof scope2.activities === "object"
        ? scope2.activities
        : {};

    return {
      ...base,
      generalInformation: {
        ...(base.generalInformation || {}),
        companyName: base.generalInformation?.companyName || baseName,
        reportingYear: base.generalInformation?.reportingYear,
      },
      scope1: {
        ...scope1,
        activities: {
          ...s1Activities,
        },
      },
      scope2: {
        ...scope2,
        activities: {
          ...s2Activities,
        },
      },
    };
  };

  const submission = detail?.submission || null;
  const orgName = detail?.organizationUnit?.name || "SME";
  const consent = detail?.consent || {};
  const consentStatus =
    consent?.lastRequest?.status || (consent?.canUse ? "approved" : "none");
  const reviewStatus = submission?.bankReview?.status || "pending";
  const canReview = submission?.status === "submitted";
  const canRequestConsent =
    submission?.status === "submitted" &&
    reviewStatus === "approved" &&
    consentStatus !== "approved" &&
    consentStatus !== "pending";

  const consentDisabledReason = (() => {
    if (!submission) return t("No submission selected.");
    if (submission.status !== "submitted")
      return t("Consent can only be requested for submitted surveys.");
    if (reviewStatus !== "approved")
      return t("Approve the submission to enable consent request.");
    if (consentStatus === "pending")
      return t("A consent request is already pending for this submission.");
    if (consentStatus === "approved")
      return t("Consent is already approved for this submission.");
    return "";
  })();

  useEffect(() => {
    const existingNote = submission?.bankReview?.note;
    if (typeof existingNote === "string") setReviewNote(existingNote);
  }, [submission?.id, submission?.bankReview?.note]);

  const reviewSubmission = async (decision) => {
    if (!submission?.id) return;
    const note = String(reviewNote || "").trim();
    if (!note) {
      showToast.error(t("Review note is required"));
      return;
    }
    setReviewing(true);
    try {
      await reviewCarbonSubmissionsReview({
        submissionId: submission.id,
        decision,
        note,
      });
      showToast.success(
        decision === "approved"
          ? t("Submission approved")
          : t("Submission rejected"),
      );
      bypass();
      navigate(PATHS.CARBON_SUBMISSIONS);
    } catch (err) {
      showToast.error(
        err?.response?.data?.message ||
          err?.message ||
          t("Failed to review submission"),
      );
    } finally {
      setReviewing(false);
    }
  };

  const requestConsent = async () => {
    if (!submission?.id) return;
    setRequesting(true);
    try {
      await axiosFetch.post(APIEndpoints.carbonBankRequestConsent, {
        organizationUnitId: detail?.organizationUnit?.id,
        submissionId: submission.id,
        message: message || "",
      });
      toast.success("Consent request sent");
      await load(year);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to send consent request",
      );
    } finally {
      setRequesting(false);
    }
  };

  const viewData = useMemo(() => {
    if (!submission?.data) return null;
    const baseName = submission.companyName || orgName;
    return normalizeData(submission.data, baseName);
  }, [submission?.data, submission?.companyName, orgName]);

  const EvidenceList = ({ files = [] }) => {
    const { t } = useTran();
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0)
      return <div className="text-xs text-slate-500">—</div>;
    return (
      <div className="space-y-1">
        {list.map((f, idx) => (
          <div key={`${f.fileId || f.url || idx}`} className="text-xs">
            {f.url ? (
              <a
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="truncate text-nxstart-blue hover:underline"
              >
                {f.name || t("Evidence file")}
              </a>
            ) : (
              <span className="truncate text-slate-600 dark:text-slate-300">
                {f.name || t("Evidence file")}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const SHOW_ACKNOWLEDGEMENT = false;

  return (
    <>
      <main className="p-6">
        <div className="mx-auto max-w-5xl space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                {t("Carbon Inventory Submission")}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{""}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate(PATHS.CARBON_SUBMISSIONS)}
              >
                {t("Back to list")}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base">{t("Submission")}</CardTitle>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <StatusBadge status={submission?.status} />
                  <ReviewBadge status={reviewStatus} />
                  {/* <ConsentBadge consent={consent} /> */}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {submission ? (
                <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-white">
                        {t("Bank review")}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {t(
                          "Approve or reject the submission (with note). If rejected, SME can edit and re-submit.",
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <ReviewBadge status={reviewStatus} />
                        {submission?.bankReview?.reviewedAt ? (
                          <span className="text-xs text-slate-600 dark:text-slate-300">
                            {`${t("Reviewed:")} ${new Date(submission.bankReview.reviewedAt).toLocaleString()}`}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => reviewSubmission("rejected")}
                        disabled={
                          !canReview || reviewing || requesting || loading
                        }
                      >
                        {reviewing ? t("Saving...") : t("Reject")}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => reviewSubmission("approved")}
                        disabled={
                          !canReview || reviewing || requesting || loading
                        }
                      >
                        {reviewing ? t("Saving...") : t("Approve")}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t("Review note")}
                    </label>
                    <Textarea
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      className="mt-1"
                      disabled={!canReview || reviewing || loading}
                      placeholder={t("Add a note for the SME (required).")}
                    />
                  </div>
                  {!canReview ? (
                    <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      {t("Only submitted surveys can be reviewed.")}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {submission && SHOW_ACKNOWLEDGEMENT ? (
                <div className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/20">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-white">
                        {t("Acknowledgement & Consent")}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {t(
                          "Bank cannot use SME data until SME approves consent. Consent requests are enabled after bank review approval.",
                        )}
                      </div>
                      {consent?.lastRequest ? (
                        <div className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                          {`${t("Last request:")} ${consent.lastRequest.status} • ${consent.lastRequest.requestedAt ? new Date(consent.lastRequest.requestedAt).toLocaleString() : "—"}`}
                        </div>
                      ) : null}
                      {!canRequestConsent && consentDisabledReason ? (
                        <div className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                          {consentDisabledReason}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="nxStartGreen"
                        onClick={requestConsent}
                        disabled={!canRequestConsent || requesting || loading}
                      >
                        {requesting ? t("Sending...") : t("Request consent")}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      {t("Message to SME")}
                    </label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="mt-1"
                      disabled={!canRequestConsent || requesting || loading}
                    />
                  </div>
                </div>
              ) : null}

              {submission && viewData ? (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        {t("General information")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          {t("Company name")}
                        </div>
                        <div className="mt-1 text-sm text-slate-900 dark:text-white">
                          {viewData.generalInformation?.companyName || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          {t("Reporting year")}
                        </div>
                        <div className="mt-1 text-sm text-slate-900 dark:text-white">
                          {viewData.generalInformation?.reportingYear || year}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                      {t("Scope 1 - Direct Emissions")}
                    </div>
                    {viewData.scope1?.mode === "total" ? (
                      <Card>
                        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <CardTitle className="text-base">
                              {t("Total Scope 1 emissions")}
                            </CardTitle>
                          </div>
                          <div className="self-start">
                            <Badge variant="outline">{t("Annual total")}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                            <div className="md:col-span-7">
                              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                {t("Annual value")}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-950/20 dark:text-white">
                                  {viewData.scope1?.totalco2e || "0"}
                                </div>
                                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-950/20 dark:text-white">
                                  {viewData.scope1?.totalco2eUnit || "-"}
                                </div>
                              </div>
                            </div>
                            <div className="md:col-span-5">
                              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                {t("Evidence (annual summary)")}
                              </div>
                              <div className="mt-1">
                                <EvidenceList
                                  files={
                                    viewData.scope1?.totalEvidenceFiles || []
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ) : viewData.scope1?.mode === "activities" ? (
                      <>
                        <CarbonActivityReportCard
                          title={t("Stationary combustion (diesel, gas, LPG)")}
                          activity={viewData.scope1?.activities?.stationary}
                        />
                        <CarbonActivityReportCard
                          title={t(
                            "Mobile combustion (company vehicles, forklifts)",
                          )}
                          activity={viewData.scope1?.activities?.mobile}
                        />
                        <CarbonActivityReportCard
                          title={t(
                            "Fugitive emissions (refrigerants/AC leaks)",
                          )}
                          activity={viewData.scope1?.activities?.fugitive}
                        />
                        <CarbonActivityReportCard
                          title={t("Process emissions (industrial gases)")}
                          activity={viewData.scope1?.activities?.process}
                        />
                      </>
                    ) : (
                      <div className="text-sm text-slate-600 dark:text-slate-300">
                        {t("Scope 1 not set.")}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                      {t("Scope 2 - Purchased Energy")}
                    </div>
                    {viewData.scope2?.mode === "total" ? (
                      <Card>
                        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <CardTitle className="text-base">
                              {t("Total Scope 2 emissions")}
                            </CardTitle>
                          </div>
                          <div className="self-start">
                            <Badge variant="outline">{t("Annual total")}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                            <div className="md:col-span-7">
                              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                {t("Annual value")}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-950/20 dark:text-white">
                                  {viewData.scope2?.totalco2e || "0"}
                                </div>
                                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-950/20 dark:text-white">
                                  {viewData.scope2?.totalco2eUnit || "-"}
                                </div>
                              </div>
                            </div>
                            <div className="md:col-span-5">
                              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                {t("Evidence (annual summary)")}
                              </div>
                              <div className="mt-1">
                                <EvidenceList
                                  files={
                                    viewData.scope2?.totalEvidenceFiles || []
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ) : viewData.scope2?.mode === "activities" ? (
                      <>
                        <CarbonActivityReportCard
                          title={t("Purchased electricity (grid or renewable)")}
                          activity={viewData.scope2?.activities?.electricity}
                        />
                        <CarbonActivityReportCard
                          title={t("Purchased steam/heat")}
                          activity={viewData.scope2?.activities?.steam}
                        />
                        <CarbonActivityReportCard
                          title={t("Purchased cooling or chilled water")}
                          activity={viewData.scope2?.activities?.cooling}
                        />
                      </>
                    ) : (
                      <div className="text-sm text-slate-600 dark:text-slate-300">
                        {t("Scope 2 not set.")}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </main>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </>
  );
});

export default BankCarbonSubmissionsFormPage;
